import { useMemo, useState } from 'react'
import { ActivitySquare, Bike, Clock, Disc3, Footprints, Gauge, ListMusic, Mic2, Music2, PersonStanding, Radio, Users2, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { StatTile } from '@/components/dashboard/StatTile'
import { AvatarLeaderboard } from '@/components/dashboard/AvatarLeaderboard'
import { PartToWholeBreakdown } from '@/components/dashboard/PartToWholeBreakdown'
import { OverplayedSongSpotlight } from '@/components/dashboard/OverplayedSongSpotlight'
import { VersatileArtistSpotlight } from '@/components/dashboard/VersatileArtistSpotlight'
import { ActivityFeed } from '@/components/dashboard/ActivityFeed'
import { useDashboardChannel, type Discipline } from '@/hooks/useDashboardChannel'
import { formatPrecise } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'

const DISCIPLINE_TABS: { value: Discipline; label: string; icon: typeof Bike }[] = [
  { value: 'all', label: 'All', icon: Radio },
  { value: 'cycling', label: 'Biking', icon: Bike },
  { value: 'running', label: 'Running', icon: PersonStanding },
  { value: 'walking', label: 'Walking', icon: Footprints },
]

// "Rides catalogued" always used the Bike icon regardless of which tab
// was selected -- fine for Biking, wrong everywhere else (and doubly so
// for All, where instructors and workouts span every discipline).
const TOTALS_ICON: Record<Discipline, typeof Bike> = {
  all: ActivitySquare,
  cycling: Bike,
  running: PersonStanding,
  walking: Footprints,
}

export function Dashboard() {
  const { data, activity, connected } = useDashboardChannel()
  const [discipline, setDiscipline] = useState<Discipline>('all')

  const stats = data?.[discipline]
  const { singular, plural, pluralCapitalized } = workoutNouns(discipline)
  const filteredActivity = useMemo(
    () => (discipline === 'all' ? activity : activity.filter((a) => a.fitness_discipline === discipline)),
    [activity, discipline],
  )

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-10">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Peloton Playlist Intelligence</h1>
          <p className="text-sm text-muted-foreground">
            What instructors actually play, at scale -- so we can find what they don&apos;t.
          </p>
        </div>
        <Badge variant="outline" className="gap-2 py-1.5">
          <span className="relative flex size-2">
            {connected && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            )}
            <span className={`relative inline-flex size-2 rounded-full ${connected ? 'bg-primary' : 'bg-muted-foreground'}`} />
          </span>
          {connected ? 'Live' : 'Connecting...'}
        </Badge>
      </header>

      <Tabs value={discipline} onValueChange={(v) => setDiscipline(v as Discipline)}>
        <TabsList>
          {DISCIPLINE_TABS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className="gap-1.5">
              <Icon className="size-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <section className="grid gap-4 lg:grid-cols-2">
        {stats?.most_overplayed_song ? (
          <OverplayedSongSpotlight song={stats.most_overplayed_song} discipline={discipline} />
        ) : (
          <Skeleton className="h-[104px] rounded-xl" />
        )}
        {stats?.most_versatile_artist ? (
          <VersatileArtistSpotlight artist={stats.most_versatile_artist} />
        ) : (
          <Skeleton className="h-[104px] rounded-xl" />
        )}
      </section>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {stats ? (
          <>
            <StatTile label={`${pluralCapitalized} catalogued`} value={stats.totals.rides} icon={TOTALS_ICON[discipline]} />
            <StatTile label="Unique songs" value={stats.totals.songs} icon={Music2} />
            <StatTile label="Unique artists" value={stats.totals.artists} icon={Mic2} />
            <StatTile label="Unique albums" value={stats.totals.albums} icon={Disc3} />
            <StatTile label="Instructors tracked" value={stats.totals.instructors} icon={Users2} />
            <StatTile label="Hours of music catalogued" value={stats.totals.total_ride_hours} icon={Clock} />
            <StatTile label="One-hit-wonder artists" value={stats.totals.one_hit_artists} icon={Zap} />
            <StatTile label={`Avg. songs per ${singular}`} value={stats.totals.avg_songs_per_ride} icon={Gauge} />
            <StatTile label="Explicit tracks" value={stats.totals.explicit_percent} icon={Radio} suffix="%" />
          </>
        ) : (
          Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-[76px] rounded-xl" />)
        )}
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ListMusic className="size-4" />
            Playlist sync progress
          </CardTitle>
          <CardDescription>
            {stats
              ? `${formatPrecise(stats.scrape_progress.rides_detailed)} of ${formatPrecise(stats.scrape_progress.rides_synced)} catalogued ${plural} have a fully synced playlist`
              : 'Loading...'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Progress value={stats?.scrape_progress.percent ?? 0} className="h-2" />
            <span className="w-14 shrink-0 text-right text-sm font-medium tabular-nums">
              {stats ? `${stats.scrape_progress.percent}%` : '--'}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Radio className="size-4" />
            Live activity
          </CardTitle>
          <CardDescription>{pluralCapitalized} as their playlists finish syncing</CardDescription>
        </CardHeader>
        <CardContent>
          <ActivityFeed entries={filteredActivity} discipline={discipline} />
        </CardContent>
      </Card>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most-played artists</CardTitle>
            <CardDescription>Ranked by how many {plural} feature them, across the whole catalog</CardDescription>
          </CardHeader>
          <CardContent>
            {stats ? (
              <AvatarLeaderboard
                entries={stats.top_artists.map((a) => ({
                  id: a.id,
                  name: a.name,
                  image_url: a.image_url,
                  count: a.play_count,
                }))}
                valueLabel={plural}
                size="lg"
              />
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Genre mix</CardTitle>
            <CardDescription>What Peloton's own song tags say the catalog leans on</CardDescription>
          </CardHeader>
          <CardContent>
            {stats ? (
              <PartToWholeBreakdown data={stats.genres} unitLabel="songs" />
            ) : (
              <Skeleton className="h-[180px]" />
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most active instructors</CardTitle>
            <CardDescription>By number of {plural} catalogued so far</CardDescription>
          </CardHeader>
          <CardContent>
            {stats ? (
              <AvatarLeaderboard
                entries={stats.top_instructors.map((i) => ({
                  id: i.id,
                  name: i.name,
                  image_url: i.image_url,
                  count: i.ride_count,
                }))}
                valueLabel={plural}
                linkTo={(entry) => `/instructors/${entry.id}`}
              />
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Popular {singular} formats</CardTitle>
            <CardDescription>Workout structure, not musical theme</CardDescription>
          </CardHeader>
          <CardContent>
            {stats ? (
              <PartToWholeBreakdown data={stats.ride_types} unitLabel={plural} />
            ) : (
              <Skeleton className="h-[240px]" />
            )}
          </CardContent>
        </Card>
      </section>

      <footer className="pb-4 text-center text-xs text-muted-foreground">
        Live over WebSockets -- updates the instant a {singular} finishes syncing.
      </footer>
    </div>
  )
}
